// Menus and overlays (DOM). Clicks are delegated through data-act attributes.
import { ICON } from './icons.js';
import { HUD, BossHUD, BlitzHUD, TitleCard } from './hud.js';
import { WORLDS, THEMES, levelName, nextLevelId } from './themes.js';
import { CATALOG, levelInfo, rankFor, nextRank, totalStars, worldStars, isLevelUnlocked, unlockMet, BADGES, DAILY, catalogItem } from './progression.js';
import { TOPICS } from './questions.js';
import { BLITZ_MODES } from './blitz.js';
import { SWORD_SHAPE } from './textures.js';
import { SWORD_PALETTES } from './models.js';
import { paintSkin, SKIN_TONES, HAIR_COLORS, HAIR_NAMES } from './skins.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { fmtNum, fmtTime, escapeHtml, todayStr, withName } from './util.js';
import { sanitiseName, nameAllowed } from './codes.js';
import { identity } from './identity.js';

const GREETINGS = (name) => [`Welcome back, ${name}! 👋`, `Ready for an adventure, ${name}?`, `The Block Realm needs you, ${name}! ⚔️`, `Let’s go, ${name}! 🚀`, `${name} is in the game! 🎮`, `Big brain time, ${name}! 🧠`, 'Hey Legend! Ready to play?'];
const TRAIL_ICONS = { none: '🚫', sparkle: '✨', notes: '🎵', hearts: '💖', fire: '🔥', diamond: '💠', rainbow: '🌈', lightning: '⚡', glitch: '👾' };
const PET_ICONS = { none: '🚫', dog: '🐕', slime: '🟢', drone: '🛸', ghost: '👻', golem: '🗿', lucky: '🍀', dragon: '🐉', tnt: '💣' };

export class UI {
  constructor(game) {
    this.game = game;
    this.root = document.getElementById('ui');
    this.screenEl = document.createElement('div');
    this.screenEl.id = 'screen';
    this.root.appendChild(this.screenEl);
    this.hud = new HUD(this);
    this.bossHud = new BossHUD(this);
    this.blitzHud = new BlitzHUD(this);
    this.card = new TitleCard(this);
    this.modalEl = document.createElement('div');
    this.modalEl.id = 'modal';
    this.root.appendChild(this.modalEl);
    this.toastEl = document.createElement('div');
    this.toastEl.id = 'toasts';
    this.root.appendChild(this.toastEl);
    this.flashEl = document.getElementById('flash');
    this.fadeEl = document.createElement('div');
    this.fadeEl.id = 'fade';
    document.body.appendChild(this.fadeEl);
    this.zoomEl = document.createElement('div');
    this.zoomEl.className = 'camera-controls';
    this.zoomEl.innerHTML = '<button data-act="zoomOut" aria-label="Zoom out" title="Zoom out (−)">−</button><button data-act="zoomReset" aria-label="Reset zoom" title="Reset zoom (0)">⌖</button><button data-act="zoomIn" aria-label="Zoom in" title="Zoom in (+)">＋</button>';
    this.root.appendChild(this.zoomEl);
    this.screen = null;
    this.worldIdx = 0;
    this.lockerTab = 'skin';
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-act]');
      if (!a || a.disabled) return;
      const handled = this.editTouchHandled;
      if (handled && e.detail && performance.now() - handled.time < 700 &&
          (a === handled.el || Math.hypot(e.clientX - handled.x, e.clientY - handled.y) < 12)) {
        e.preventDefault();
        return;
      }
      if (this.game.input.layout.editing && !a.dataset.act.startsWith('touchLayout')) return;
      e.preventDefault();
      this.act(a.dataset.act, a.dataset, a);
    });
    // Touch releases are reliable after device rotation even when a browser
    // suppresses the later compatibility click. Only editor buttons use this.
    document.addEventListener('pointerdown', e => {
      const el = e.target.closest('.control-layout-actions [data-act]');
      if (e.pointerType === 'touch' && el && this.game.input.layout.editing)
        this.editTouch = { id: e.pointerId, x: e.clientX, y: e.clientY, el };
    });
    document.addEventListener('pointerup', e => {
      const tap = this.editTouch;
      if (!tap || e.pointerId !== tap.id) return;
      this.editTouch = null;
      if (Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > 12 ||
          !tap.el.contains(document.elementFromPoint(e.clientX, e.clientY))) return;
      e.preventDefault();
      this.editTouchHandled = { el: tap.el, x: e.clientX, y: e.clientY, time: performance.now() };
      this.act(tap.el.dataset.act, tap.el.dataset, tap.el);
    });
    document.addEventListener('pointercancel', e => {
      if (this.editTouch?.id === e.pointerId) this.editTouch = null;
    });
    document.addEventListener('input', (e) => {
      const a = e.target.closest('[data-input]');
      if (a) this.onInput(a.dataset.input, a);
    });
    // Enter finishes the sign-in and visit-a-friend boxes.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      if (e.target.closest?.('.who-name')) { e.preventDefault(); this.act('whoLogin', {}, e.target); }
      else if (e.target.closest?.('.friends-code')) { e.preventDefault(); this.act('joinWorld', {}, e.target); }
    });
    document.addEventListener(
      'pointerenter',
      (e) => {
        if (e.target.classList && e.target.classList.contains('btn') && e.pointerType === 'mouse') audio.play('hover');
      },
      true
    );
  }

  get save() {
    return this.game.save.data;
  }

  // ---------- helpers ----------
  faceCanvas(size = 48) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    const skin = this.game.currentSkin();
    ctx.drawImage(skin.canvas, 8, 8, 8, 8, 0, 0, size, size);
    ctx.drawImage(skin.canvas, 40, 8, 8, 8, 0, 0, size, size);
    c.className = 'face';
    return c;
  }

  skinPreview(outfit) {
    const s = this.save.avatar;
    const skin = paintSkin(outfit, s.tone, s.hair, s.style);
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 128;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    const k = 4;
    const d = (sx, sy, sw, sh, dx, dy) => ctx.drawImage(skin.canvas, sx, sy, sw, sh, dx * k, dy * k, sw * k, sh * k);
    d(8, 8, 8, 8, 4, 0); // head
    d(40, 8, 8, 8, 4, 0); // hat
    d(20, 20, 8, 12, 4, 8); // body
    d(44, 20, 4, 12, 0, 8); // right arm
    d(36, 52, 4, 12, 12, 8); // left arm
    d(4, 20, 4, 12, 4, 20); // right leg
    d(20, 52, 4, 12, 8, 20); // left leg
    return c.toDataURL();
  }

  swordPreview(kind) {
    const pal = SWORD_PALETTES[kind] || SWORD_PALETTES.wood;
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const ctx = c.getContext('2d');
    SWORD_SHAPE.forEach((row, y) =>
      [...row].forEach((ch, x) => {
        let col = pal[ch];
        if (!col) return;
        col = col.replace('!', '');
        if (kind === 'rainbow' && (ch === 'w' || ch === 'l')) col = ['#ff4d6d', '#ffb020', '#ffe03b', '#4bd34b', '#3bb0ff', '#a05cff'][(x + y) % 6];
        ctx.fillStyle = col;
        ctx.fillRect(x * 4, y * 4, 4, 4);
      })
    );
    return c.toDataURL();
  }

  currencies() {
    const s = this.save;
    return `<div class="currencies"><div class="pill coin-pill">${ICON.coin}<span>${fmtNum(s.coins)}</span></div><div class="pill gem-pill">${ICON.gem}<span>${fmtNum(s.gems)}</span></div></div>`;
  }

  playerCard() {
    const s = this.save;
    const li = levelInfo(s.xp);
    const rk = rankFor(li.level);
    return `<div class="player-card" data-act="stats">
      <div class="pc-face-wrap"><span class="pc-face-slot"></span><span class="pc-lvl">${li.level}</span></div>
      <div class="pc-info">
        <div class="pc-name">${escapeHtml(s.name.toUpperCase())} <span class="rank" style="--rc:${rk.color}">${escapeHtml(this.rankName(rk))}</span></div>
        <div class="xpbar"><div class="xpfill" style="width:${li.pct * 100}%"></div><span>Level ${li.level} · ${li.into}/${li.need} XP</span></div>
      </div>
    </div>`;
  }

  // Plain text: the top rank carries the player's name in capitals.
  rankName(rk) {
    return withName(rk.name, this.save.name.toUpperCase());
  }

  mountFace(scope) {
    const slot = scope.querySelector('.pc-face-slot');
    if (slot) slot.replaceWith(this.faceCanvas(52));
  }

  setScreen(html, cls) {
    this.screenEl.className = cls || '';
    this.screenEl.innerHTML = html;
    this.screenEl.classList.add('show');
    this.mountFace(this.screenEl);
  }

  hideScreen() {
    this.screenEl.classList.remove('show');
    this.screenEl.innerHTML = '';
    this.screen = null;
  }

  // ---------- main menu ----------
  showMain() {
    this.screen = 'main';
    const s = this.save;
    const dailyReady = s.daily.lastClaim !== todayStr();
    const greetings = GREETINGS(escapeHtml(s.name));
    const greet = greetings[Math.floor(Math.random() * greetings.length)];
    const stars = totalStars(s);
    this.setScreen(
      `<div class="topbar">${this.playerCard()}<div class="tr-group">${this.currencies()}<button class="btn btn-icon daily-btn ${dailyReady ? 'ready' : ''}" data-act="daily" title="Daily reward">🎁</button></div></div>
      <div class="menu-bottom">
        <div class="tagline">${greet}</div>
        <div class="mode-choices"><button class="mode-card mode-adventure" data-act="play"><span class="mode-icon">⚔️</span><small>LET’S PLAY</small><strong>Adventure</strong><em>Run, jump &amp; solve puzzles</em><b>Play adventure →</b></button>
        <button class="mode-card mode-explore" data-act="explore"><span class="mode-icon">🌍</span><small>OR TRY SOMETHING DIFFERENT</small><strong>Explore &amp; Craft</strong><em>Gather, learn &amp; build your world</em><b>Enter your world →</b></button></div>
        <div class="menu-row">
          <button class="btn btn-green" data-act="friends">👥 Friends</button>
          <button class="btn btn-purple" data-act="blitz">⚡ Brain Blitz</button>
          <button class="btn btn-yellow" data-act="locker">🎒 Locker</button>
          <button class="btn btn-orange" data-act="badges">🏆 Badges</button>
          <button class="btn btn-blue" data-act="stats">🧠 Brain Stats</button>
          <button class="btn btn-dark btn-icon" data-act="settings" title="Settings">⚙️</button>
        </div>
        <div class="menu-foot">⭐ ${stars} stars · 🏅 ${Object.keys(s.ach).length}/${BADGES.length} badges</div>
      </div>`,
      'scr-main'
    );
  }

  // ---------- world select ----------
  showWorlds(wi) {
    this.screen = 'worlds';
    const s = this.save;
    if (wi !== undefined) this.worldIdx = wi;
    const W = WORLDS[this.worldIdx];
    const unlocked = isLevelUnlocked(s, `${W.id}-1`);
    const nodes = ['1', '2', '3', 'B']
      .map((l, i) => {
        const id = `${W.id}-${l}`;
        const st = s.levels[id];
        const open = isLevelUnlocked(s, id);
        const stars = st?.stars || [0, 0, 0];
        const cur = open && !st?.done;
        return `<button class="lvl-node ${l === 'B' ? 'boss' : ''} ${open ? '' : 'locked'} ${st?.done ? 'done' : ''} ${cur ? 'current' : ''}" data-act="pick" data-id="${id}" style="--wc:${W.color}">
          <div class="ln-num">${l === 'B' ? '👑' : i + 1}</div>
          <div class="ln-name">${open ? levelName(id) : levelName(id)}</div>
          <div class="ln-stars">${stars.map((x) => `<span class="${x ? 'on' : ''}">${ICON.star}</span>`).join('')}</div>
          ${cur ? '<div class="ln-next">PLAY NEXT ▶</div>' : ''}${open ? '' : `<div class="ln-lock">${ICON.lock}</div><small>Finish ${i===0?'the last boss':'level '+i} first</small>`}
          ${st?.best ? `<div class="ln-best">⏱ ${fmtTime(st.best)}</div>` : ''}
        </button>${i < 3 ? '<div class="ln-link"></div>' : ''}`;
      })
      .join('');
    this.setScreen(
      `<div class="topbar"><button class="btn btn-dark" data-act="home">← Menu</button>
        <div class="w-title" style="--wc:${W.color}"><div class="w-num">WORLD ${W.id}</div><div class="w-name">${W.name}</div></div>
        ${this.currencies()}</div>
      <button class="w-arrow left" data-act="wprev" ${this.worldIdx === 0 ? 'disabled' : ''}>◀</button>
      <button class="w-arrow right" data-act="wnext" ${this.worldIdx === WORLDS.length - 1 ? 'disabled' : ''}>▶</button>
      <div class="w-bottom panel ${unlocked ? '' : 'w-locked'}">
        <div class="w-blurb">${unlocked ? 'Tap the glowing level. Follow the coins to the portal!' : `🔒 Defeat the World ${W.id - 1} boss to unlock ${W.name}!`}</div>
        <div class="w-levels">${nodes}</div>
        <div class="expedition-links"><strong>NEW DISCOVERY ADVENTURES</strong><button class="btn btn-small btn-purple" data-act="expedition" data-id="mine">🛤️ Crystal Mine</button><button class="btn btn-small btn-blue" data-act="expedition" data-id="sky">🐉 Sky Rescue</button><button class="btn btn-small btn-orange" data-act="expedition" data-id="temple">🏛️ Secret Temple</button></div><div class="w-foot"><span>⭐ ${worldStars(s, W.id)} / 12</span><span class="w-dots">${WORLDS.map((w, i) => `<i class="${i === this.worldIdx ? 'on' : ''}" style="--wc:${w.color}"></i>`).join('')}</span></div>
      </div>`,
      'scr-worlds'
    );
    this.game.menuScene.setTheme(W.theme);
  }

  // ---------- locker ----------
  showLocker(tab) {
    this.screen = 'locker';
    if (tab) this.lockerTab = tab;
    const s = this.save;
    const tabs = [
      ['skin', '👕 Skins'],
      ['sword', '⚔️ Swords'],
      ['trail', '✨ Trails'],
      ['pet', '🐾 Pets'],
      ['look', '🎨 Look'],
    ];
    let body = '';
    if (this.lockerTab === 'look') {
      const a = s.avatar;
      body = `<div class="look">
        <h3>Skin tone</h3><div class="swatches">${SKIN_TONES.map((c, i) => `<button class="swatch ${a.tone === i ? 'on' : ''}" style="background:${c}" data-act="tone" data-i="${i}"></button>`).join('')}</div>
        <h3>Hair colour</h3><div class="swatches">${HAIR_COLORS.map((c, i) => `<button class="swatch ${a.hair === i ? 'on' : ''}" style="background:${c}" title="${HAIR_NAMES[i]}" data-act="hair" data-i="${i}"></button>`).join('')}</div>
        <h3>Hair style</h3><div class="chips">${['short', 'spiky', 'long'].map((st) => `<button class="chip ${a.style === st ? 'on' : ''}" data-act="hstyle" data-s="${st}">${st[0].toUpperCase() + st.slice(1)}</button>`).join('')}</div>
        <p class="hint">Make ${escapeHtml(s.name)} look just like you! (Some outfits like helmets cover the hair.)</p>
      </div>`;
    } else {
      const cat = this.lockerTab;
      body = `<div class="items-grid">${CATALOG[cat]
        .map((it) => {
          const owned = s.owned[cat].includes(it.id);
          const equipped = s.equip[cat] === it.id;
          const locked = !owned && ((it.unlock && !unlockMet(s, it.unlock)) || (it.learn||0)>s.stats.correct);
          const cur = it.cur === 'gems' ? 'gems' : 'coins';
          const afford = !owned && !locked && s[cur] >= (it.price || 0);
          let prev = '';
          if (cat === 'skin') prev = `<img class="ic-img skin" src="${this.skinPreview(it.id)}" alt="">`;
          else if (cat === 'sword') prev = it.id ? `<img class="ic-img sword" src="${this.swordPreview(it.id)}" alt="">` : '';
          else if (cat === 'trail') prev = `<div class="ic-emoji">${TRAIL_ICONS[it.id] || '✨'}</div>`;
          else prev = `<div class="ic-emoji">${PET_ICONS[it.id] || '🐾'}</div>`;
          let tag;
          if (equipped) tag = `<div class="ic-tag eq">${ICON.check} EQUIPPED</div>`;
          else if (owned) tag = `<div class="ic-tag own">TAP TO WEAR</div>`;
          else if (locked) tag = `<div class="ic-tag lock">${ICON.lock} ${(it.learn||0)>s.stats.correct ? `${Math.min(s.stats.correct,it.learn)}/${it.learn} learned answers` : it.unlock.startsWith('level') ? 'Level ' + it.unlock.split(':')[1] : it.unlock.startsWith('boss') ? 'Beat W' + it.unlock.split(':')[1] + ' boss' : it.unlock.startsWith('topics') ? 'Top level in ' + it.unlock.split(':')[1] + ' topics' : 'Locked'}</div>`;
          else tag = `<div class="ic-tag price ${afford ? '' : 'poor'}">${cur === 'gems' ? ICON.gem : ICON.coin} ${fmtNum(it.price)}</div>`;
          return `<button class="item-card ${equipped ? 'equipped' : ''} ${owned ? 'owned' : ''} ${locked ? 'locked' : ''}" data-act="item" data-cat="${cat}" data-id="${it.id}">
            <div class="ic-prev">${prev}</div><div class="ic-name">${withName(it.name, escapeHtml(s.name))}</div>${it.desc ? `<div class="ic-desc">${it.desc}</div>` : ''}${tag}</button>`;
        })
        .join('')}</div>`;
    }
    this.setScreen(
      `<div class="topbar"><button class="btn btn-dark" data-act="home">← Menu</button><div class="scr-title">🎒 ${escapeHtml(s.name.toUpperCase())}'S LOCKER</div>${this.currencies()}</div>
      <div class="locker-panel panel">
        <div class="tabs">${tabs.map(([k, n]) => `<button class="tab ${this.lockerTab === k ? 'on' : ''}" data-act="ltab" data-tab="${k}">${n}</button>`).join('')}</div>
        ${body}
      </div>
      <div class="locker-hint">Drag to spin ${escapeHtml(s.name)}! 👉</div>`,
      'scr-locker'
    );
  }

  // ---------- badges ----------
  showBadges() {
    this.screen = 'badges';
    const s = this.save;
    const got = Object.keys(s.ach).length;
    this.setScreen(
      `<div class="topbar"><button class="btn btn-dark" data-act="home">← Menu</button><div class="scr-title">🏆 BADGES <small>${got} / ${BADGES.length}</small></div>${this.currencies()}</div>
      <div class="panel big-panel"><div class="badge-grid">${BADGES.map((b) => {
        const has = s.ach[b.id];
        const rw = b.reward.gems ? `${ICON.gem} ${b.reward.gems}` : `${ICON.coin} ${b.reward.coins}`;
        return `<div class="badge ${has ? 'got' : ''}"><div class="b-icon">${b.icon}</div><div class="b-name">${b.name}</div><div class="b-desc">${b.desc}</div><div class="b-rw">${has ? '✅ Earned!' : rw}</div></div>`;
      }).join('')}</div></div>`,
      'scr-badges'
    );
  }

  // ---------- brain stats ----------
  showStats() {
    this.screen = 'stats';
    const s = this.save;
    const st = s.stats;
    const qe = this.game.qe;
    const acc = st.answered ? Math.round((st.firstTry / st.answered) * 100) : 0;
    const topics = Object.entries(TOPICS)
      .map(([k, t]) => {
        const ts = s.topics[k];
        const lvl = ts ? Math.floor(ts.lvl) : t.start;
        const a = ts && ts.n ? Math.round((ts.c / ts.n) * 100) : null;
        const off = s.settings.topics[k] === false;
        return `<div class="topic ${off ? 'off' : ''}"><span class="t-ico">${t.icon}</span><span class="t-name">${t.name}</span><span class="t-lvl">${Array.from({ length: t.max }, (_, i) => `<i class="${i < lvl ? 'on' : ''}"></i>`).join('')}</span><span class="t-acc">${a === null ? '—' : a + '%'}</span></div>`;
      })
      .join('');
    let grid = '<div class="tt-grid"><div class="tt-h"></div>';
    for (let c = 1; c <= 12; c++) grid += `<div class="tt-h">${c}</div>`;
    for (let r = 2; r <= 12; r++) {
      grid += `<button class="tt-h tt-row" data-act="practice" data-t="${r}" title="Practise the ${r}s">${r}×</button>`;
      for (let c = 1; c <= 12; c++) {
        const m = qe.factMastery(r, c);
        grid += `<div class="tt-c m${m}" title="${r} × ${c} = ${r * c}">${r * c}</div>`;
      }
    }
    grid += '</div>';
    const mastered = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter((t) => qe.tableMastered(t));
    this.setScreen(
      `<div class="topbar"><button class="btn btn-dark" data-act="home">← Menu</button><div class="scr-title">🧠 ${escapeHtml(s.name.toUpperCase())}'S BRAIN STATS</div>${this.currencies()}</div>
      <div class="panel big-panel stats-panel">
        <div class="stat-cards">
          <div class="sc"><b>${fmtNum(st.correct)}</b><span>right answers</span></div>
          <div class="sc"><b>${acc}%</b><span>right first time</span></div>
          <div class="sc"><b>${st.bestStreak}</b><span>best streak 🔥</span></div>
          <div class="sc"><b>${fmtTime(st.playTime)}</b><span>time played</span></div>
          <div class="sc"><b>${totalStars(s)}</b><span>stars ⭐</span></div>
        </div>
        <div class="stats-cols">
          <div><h3>Brain levels</h3><div class="topics">${topics}</div></div>
          <div><h3>Times tables ${mastered.length ? `<small>· mastered: ${mastered.map((t) => t + 's').join(', ')}</small>` : ''}</h3>${grid}
            <div class="tt-key"><span class="m0"></span>not tried <span class="m1"></span>practise <span class="m2"></span>learning <span class="m3"></span>mastered! · Tap a row to practise it in Brain Blitz</div></div>
        </div>
      </div>`,
      'scr-stats'
    );
  }

  // ---------- settings ----------
  showSettings(fromPause = false) {
    this.screen = 'settings';
    this.settingsFromPause = fromPause;
    const s = this.save;
    const S = s.settings;
    const tables = S.tables || [];
    const html = `<div class="panel settings-panel">
      <div class="set-head"><h2>⚙️ Settings</h2><button class="btn btn-dark btn-small" data-act="${fromPause ? 'closeSettings' : 'home'}">✖ Close</button></div>
      <div class="set-scroll">
      <section><h3>🔊 Sound</h3>
        <label class="row">🎵 Music <input type="range" min="0" max="1" step="0.05" value="${S.music}" data-input="music"></label>
        <label class="row">💥 Sound effects <input type="range" min="0" max="1" step="0.05" value="${S.sfx}" data-input="sfx"></label>
        <label class="row">🗣️ Read questions out loud <button class="toggle ${S.readAloud ? 'on' : ''}" data-act="toggle" data-k="readAloud"></button></label>
      </section>
      <section><h3>🎮 Game</h3>
        <label class="row">🦘 Auto-jump up blocks <button class="toggle ${S.autoJump ? 'on' : ''}" data-act="toggle" data-k="autoJump"></button></label>
        <label class="row"><span>📳 Vibration<br><small>Little buzzes for jumps, hits and right answers. Phones and tablets only.</small></span><button class="toggle ${S.haptics ? 'on' : ''}" data-act="toggle" data-k="haptics"></button></label>
        <div class="row">🖥️ Graphics <div class="chips">${['auto', 'low', 'medium', 'high'].map((q) => `<button class="chip ${S.quality === q ? 'on' : ''}" data-act="quality" data-q="${q}">${q[0].toUpperCase() + q.slice(1)}</button>`).join('')}</div></div>
        <div class="row">✏️ Hero name <input class="name-input" maxlength="12" value="${escapeHtml(s.name)}" data-input="name"></div>
      </section>
      <section><h3>🧑‍🏫 Learning <small>(for grown-ups)</small></h3>
        <div class="row">Difficulty <div class="chips">${[['easy', 'Easier'], ['auto', 'Auto ✨'], ['hard', 'Harder']].map(([k, n]) => `<button class="chip ${S.difficulty === k ? 'on' : ''}" data-act="diff" data-d="${k}">${n}</button>`).join('')}</div></div>
        <div class="row col">Topics <div class="chips wrap">${Object.entries(TOPICS).map(([k, t]) => `<button class="chip ${S.topics[k] === false ? '' : 'on'}" data-act="topic" data-t="${k}">${t.icon} ${t.name}</button>`).join('')}</div></div>
        <div class="row col">Times tables to focus on <small>(none selected = follow the worlds)</small><div class="chips wrap">${[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((t) => `<button class="chip ${tables.includes(t) ? 'on' : ''}" data-act="ttable" data-t="${t}">${t}×</button>`).join('')}</div></div>
        <p class="hint">“Auto” adapts to ${escapeHtml(s.name)}: three right answers in a row makes questions a little harder, a wrong answer makes them easier.</p>
      </section>
      <section><h3>👥 Friends <small>(for grown-ups)</small></h3>
        <label class="row">🌍 Show my world in “Friends playing now” <button class="toggle ${S.worldListed !== false ? 'on' : ''}" data-act="toggle" data-k="worldListed"></button></label>
        <p class="hint">When this is off, friends can still visit with your world code.</p>
      </section>
      <section><h3>☁️ Save</h3>
        ${identity.loggedIn ? `<p>Playing as <b>${escapeHtml(identity.data.name)}</b>. Sign in with your name and birthday month on any device to carry on.</p><div class="code-row"><button class="btn btn-small btn-blue" data-act="switchPlayer">Switch player</button></div>` : ''}
        <p>Progress saves automatically on this device <b>and</b> to the cloud. To play on another device, enter this code there:</p>
        <div class="code-row"><code class="save-code">${s.cloudCode}</code><button class="btn btn-small btn-blue" data-act="copyCode">Copy</button></div>
        <div class="code-row"><input class="code-input" placeholder="ENTER-SAVE-CODE" data-input="restore"><button class="btn btn-small btn-green" data-act="restore">Load save</button></div>
        <p class="restore-msg"></p>
        <button class="btn btn-small btn-red" data-act="reset">Reset all progress…</button>
      </section>
      <p class="about">SAICRAFT · made especially for Sai 💛</p>
      </div></div>`;
    if (fromPause) {
      this.modal(html, 'modal-settings');
    } else this.setScreen(`<div class="settings-wrap">${html}</div>`, 'scr-settings');
  }

  onInput(k, el) {
    const s = this.save;
    if (k === 'music' || k === 'sfx') {
      s.settings[k] = parseFloat(el.value);
      audio.setVolumes(s.settings.music, s.settings.sfx);
      if (k === 'sfx') audio.play('coin');
      this.game.save.persist();
    } else if (k === 'name') {
      const v = sanitiseName(el.value);
      s.name = nameAllowed(v) ? v : s.name || 'Sai';
      this.game.save.persist();
      this.game.refreshCharacter();
    }
  }

  // ---------- modals ----------
  modal(html, cls = '') {
    this.modalEl.className = 'show ' + cls;
    this.modalEl.innerHTML = `<div class="modal-back"></div><div class="modal-body">${html}</div>`;
  }
  closeModal() {
    this.game.input.layout.finish(false);
    this.modalEl.className = '';
    this.modalEl.innerHTML = '';
    if (this.modalResolve) {
      const r = this.modalResolve;
      this.modalResolve = null;
      r(false);
    }
  }
  get modalOpen() {
    return this.modalEl.classList.contains('show');
  }

  confirm(title, text, yes = 'Yes', no = 'Cancel') {
    return new Promise((resolve) => {
      this.modal(`<div class="panel confirm-panel"><h2>${title}</h2><p>${text}</p><div class="btn-row"><button class="btn btn-dark" data-act="confirmNo">${no}</button><button class="btn btn-green" data-act="confirmYes">${yes}</button></div></div>`);
      this.modalResolve = resolve;
    });
  }

  showPause() {
    const L = this.game.level;
    const goals = this.game.explore ? '🌍 Your inventory and buildings save automatically.' : L
      ? L.isBoss
        ? `⭐ Beat the boss · 🧠 80% right first time · ❤️ take 1 hit or less`
        : `⭐ Reach the portal · 🧠 80% right first time (${L.stats.asked ? Math.round((L.stats.firstTry / L.stats.asked) * 100) + '%' : '—'}) · 🔍 Secret star ${L.stats.secret ? '✅' : '❌'}`
      : '';
    this.modal(
      `<div class="panel pause-panel"><h2>⏸ PAUSED</h2>
        <div class="pause-goals">${goals}</div>
        <button class="btn btn-green btn-big" data-act="resume">▶ Keep playing</button>
        <div class="btn-row">
          ${this.game.explore ? '' : '<button class="btn btn-blue" data-act="restart">↻ Restart</button>'}
          <button class="btn btn-dark" data-act="pauseSettings">⚙️ Settings</button>
          <button class="btn btn-blue" data-act="modeHome">⌂ Home screen · swap modes</button>
        </div>
        ${this.game.input.touchMode ? '<button class="btn btn-purple" data-act="touchLayout">🕹️ Move controls</button>' : ''}
        <div class="controls-help">${this.game.input.touchMode ? '🕹️ Left side: move · Right side: look around · two fingers: pinch to zoom · ⤒ jump · ⚔️ hit / solve' : '⌨️ WASD / arrows move · SPACE jump · E / click hit &amp; solve · drag mouse to look · scroll to zoom'}</div>
      </div>`,
      'modal-pause'
    );
  }

  showTouchLayout() {
    if (!this.game.paused || this.game.quiz.open) return;
    this.game.input.setTouchMode(true);
    this.modal(`<div class="panel control-layout-card"><h2>Make it comfy 🕹️</h2><p>Drag Move, Jump, Use and Zoom.<br>This <span class="control-layout-orientation">${innerWidth >= innerHeight ? 'landscape' : 'portrait'}</span> layout stays on this device.</p><div class="control-layout-actions"><button class="btn btn-green" data-act="touchLayoutDone">✓ Save</button><button class="btn btn-dark" data-act="touchLayoutReset">Reset</button><button class="btn btn-dark" data-act="touchLayoutCancel">Cancel</button></div><span class="control-layout-status" role="status"></span></div>`, 'modal-control-layout');
    this.game.input.layout.begin();
  }

  showDaily() {
    const s = this.save;
    const d = s.daily;
    const claimed = d.lastClaim === todayStr();
    const day = claimed ? d.day : d.day % 7;
    this.modal(
      `<div class="panel daily-panel"><h2>🎁 Daily Reward</h2>
      <p>Come back every day for bigger rewards, ${escapeHtml(s.name)}!</p>
      <div class="daily-days">${DAILY.map((r, i) => {
        const done = claimed ? i < d.day % 7 || (d.day % 7 === 0 && d.day > 0) : i < day;
        const today = !claimed && i === day;
        return `<div class="dd ${done ? 'done' : ''} ${today ? 'today' : ''} ${r.mega ? 'mega' : ''}"><small>Day ${i + 1}</small><div class="dd-ico">${r.mega ? '🧰' : r.gems && !r.coins ? ICON.gem : ICON.coin}</div><b>${r.coins ? fmtNum(r.coins) : ''}${r.coins && r.gems ? ' + ' : ''}${r.gems ? r.gems + ' 💎' : ''}</b>${done ? '<i>✓</i>' : ''}</div>`;
      }).join('')}</div>
      <div class="daily-streak">${s.daily.streak ? `🔥 ${s.daily.streak} day streak!` : '🔥 Start your streak today!'}</div>
      ${claimed ? `<p class="hint">Already claimed today — come back tomorrow! 😴</p><button class="btn btn-dark" data-act="closeModal">OK</button>` : `<button class="btn btn-green btn-big claim-btn" data-act="claimDaily">CLAIM!</button>`}
      </div>`,
      'modal-daily'
    );
  }

  showLevelUp(level, rankChanged, reward) {
    return new Promise((resolve) => {
      const rk = rankFor(level);
      const nr = nextRank(level);
      this.modal(
        `<div class="levelup"><div class="lu-rays"></div><div class="lu-text">LEVEL UP!</div><div class="lu-num">${level}</div>
        ${rankChanged ? `<div class="lu-rank">NEW RANK: <span style="color:${rk.color}">${escapeHtml(this.rankName(rk).toUpperCase())}</span>!</div>` : `<div class="lu-rank sub">${nr ? `Next rank: ${escapeHtml(this.rankName(nr))} at level ${nr.at}` : `You are the ${escapeHtml(this.rankName(rk))}!`}</div>`}
        <div class="lu-reward">+${reward} ${ICON.coin}</div>
        <button class="btn btn-yellow btn-big" data-act="closeModal">AWESOME! 🎉</button></div>`,
        'modal-levelup'
      );
      audio.play('levelUp');
      this.modalResolve = () => resolve();
    });
  }

  showIntro() {
    const pages = [
      ['🐾', `Hi, <b>${escapeHtml(this.save.name)}!</b> Start with <b>Adventure</b>: follow the coins, jump and solve puzzles. Or choose <b>Explore &amp; Craft</b> to gather, craft and build with Scout.`],
      ['🌍', '<b>Choose your kind of fun.</b> Jump through puzzles in Adventure, or gather and build in Explore &amp; Craft. Look for <b>?</b> when you need help!'],
    ];
    let i = 0;
    return new Promise((resolve) => {
      const render = () => {
        const [ico, txt] = pages[i];
        this.modal(
          `<div class="panel intro-panel"><div class="intro-ico">${ico}</div><p>${txt}</p><div class="intro-dots">${pages.map((_, k) => `<i class="${k === i ? 'on' : ''}"></i>`).join('')}</div>
          <button class="btn ${i === pages.length - 1 ? 'btn-green btn-big' : 'btn-blue'}" data-act="introNext">${i === pages.length - 1 ? "LET'S GO! ▶" : 'Next ➜'}</button></div>`,
          'modal-intro'
        );
      };
      this.introNext = () => {
        i++;
        if (i >= pages.length) {
          this.introNext = null;
          this.closeModal();
          resolve();
        } else {
          audio.play('pop');
          render();
        }
      };
      render();
    });
  }

  showBlitzSelect() {
    const s = this.save;
    const top = (s.blitz.scores || []).slice(0, 5);
    this.modal(
      `<div class="panel blitz-panel"><div class="set-head"><h2>⚡ Brain Blitz</h2><button class="btn btn-dark btn-small" data-act="closeModal">✖</button></div>
      <p>60 seconds! Run to the block with the right answer and smash it. Right answers give <b>+2 seconds</b> and build your 🔥 combo!</p>
      <div class="blitz-modes">${Object.entries(BLITZ_MODES).map(([k, m]) => `<button class="blitz-mode" data-act="blitzGo" data-mode="${k}"><span class="bm-ico">${m.icon}</span><span>${m.name}</span></button>`).join('')}</div>
      <div class="blitz-best">🏆 Best: <b>${fmtNum(s.blitz.best || 0)}</b>${top.length ? ` · Top scores: ${top.map((x) => fmtNum(x.s)).join(', ')}` : ''}</div>
      ${s.settings.tables?.length ? `<p class="hint">Times tables focus: ${s.settings.tables.map((t) => t + '×').join(' ')} (change in Settings)</p>` : ''}
      </div>`,
      'modal-blitz'
    );
  }

  showBlitzResults(r) {
    const s = this.save;
    this.setScreen(
      `<div class="res-wrap"><div class="panel res-panel">
        <div class="res-banner">${r.newBest ? '🏆 NEW HIGH SCORE! 🏆' : "TIME'S UP!"}</div>
        <div class="blitz-score">${fmtNum(r.score)}</div>
        <div class="res-stats">
          <div><span>✅ Right</span><b>${r.correct}</b></div>
          <div><span>🔥 Best combo</span><b>${r.bestCombo}</b></div>
          <div><span>${ICON.coin} Coins</span><b>+${r.coins}</b></div>
          <div><span>${ICON.gem} Gems</span><b>+${r.gems}</b></div>
          <div><span>✨ XP</span><b>+${r.xp}</b></div>
        </div>
        <div class="blitz-best">Best ever: <b>${fmtNum(s.blitz.best)}</b></div>
        <div class="res-btns"><button class="btn btn-dark" data-act="home">🏠 Menu</button><button class="btn btn-purple btn-big" data-act="blitzGo" data-mode="${r.mode}">↻ Play again</button></div>
      </div></div>`,
      'scr-results'
    );
    if (r.newBest) this.game.fx.particles.confetti(this.game.camera.position.clone().add(this.game.camera.getWorldDirection(new this.game.THREE.Vector3()).multiplyScalar(6)), 80, 3);
  }

  // ---------- results ----------
  async showResults(res, info) {
    this.screen = 'results';
    const s = this.save;
    const labels = res.isBoss ? ['Boss defeated!', 'Brain Star (80%+)', 'Took 1 hit or less'] : ['Level complete!', 'Brain Star (80%+)', 'Secret star found'];
    const next = nextLevelId(res.id);
    const nextOpen = next && isLevelUnlocked(s, next);
    const acc = res.asked ? Math.round(res.accuracy * 100) : 100;
    this.setScreen(
      `<div class="res-wrap"><div class="panel res-panel">
        <div class="res-banner">${res.isBoss ? 'BOSS DEFEATED!' : 'LEVEL COMPLETE!'}</div>
        <div class="res-sub">${res.isBoss ? `You saved ${WORLDS[parseInt(res.id, 10) - 1].name}, ${escapeHtml(s.name)}! 🎉` : `Awesome work, ${escapeHtml(s.name)}! 🎉`}</div>
        <div class="res-stars">${res.stars.map((on, i) => `<div class="rs" data-i="${i}"><div class="rs-star ${on ? 'earn' : ''}">${ICON.star}</div><small>${labels[i]}</small></div>`).join('')}</div>
        <div class="res-stats">
          <div><span>${ICON.coin} Coins</span><b class="count" data-to="${res.coins}">0</b></div>
          <div><span>${ICON.gem} Gems</span><b class="count" data-to="${res.gems}">0</b></div>
          <div><span>🧠 Right first time</span><b>${res.asked ? `${res.firstTry}/${res.asked}` : '—'} <small>${res.asked ? acc + '%' : ''}</small></b></div>
          <div><span>⏱ Time</span><b>${fmtTime(res.time)}${info.newBestTime ? ' <small class="nb">NEW BEST!</small>' : ''}</b></div>
          <div><span>⚔️ Monsters</span><b>${res.mobs}</b></div>
        </div>
        <div class="res-xp"><div class="xpbar big"><div class="xpfill" style="width:${info.xpFrom.pct * 100}%"></div><span>Level ${info.xpFrom.level}</span></div><b class="xpgain">+${res.xp} XP</b></div>
        <div class="res-unlocks">${info.unlocks.map((u) => `<div class="unlock">${u}</div>`).join('')}</div>
        <div class="res-btns">
          <button class="btn btn-dark" data-act="map">🗺️ Map</button>
          <button class="btn btn-blue" data-act="replay" data-id="${res.id}">↻ Replay</button>
          ${nextOpen ? `<button class="btn btn-green btn-big" data-act="pick" data-id="${next}">NEXT ▶</button>` : res.id === '5-B' ? `<button class="btn btn-green btn-big" data-act="home">🏆 THE END… for now!</button>` : ''}
        </div>
      </div></div>`,
      'scr-results'
    );
    const el = this.screenEl;
    // star reveal
    const starsEls = [...el.querySelectorAll('.rs-star')];
    for (let i = 0; i < starsEls.length; i++) {
      await wait(380);
      starsEls[i].classList.add('show');
      if (res.stars[i]) audio.play('star');
      else audio.play('pop');
    }
    // count up
    for (const c of el.querySelectorAll('.count')) countUp(c, parseInt(c.dataset.to, 10), 700);
    audio.play('coin');
    await wait(500);
    // xp bar fill (with level ups)
    const fill = el.querySelector('.res-xp .xpfill');
    const label = el.querySelector('.res-xp span');
    const to = info.xpTo;
    if (fill) {
      if (to.level > info.xpFrom.level) {
        fill.style.width = '100%';
        await wait(650);
        fill.style.transition = 'none';
        fill.style.width = '0%';
        void fill.offsetWidth;
        fill.style.transition = '';
      }
      fill.style.width = to.pct * 100 + '%';
      label.textContent = `Level ${to.level} · ${to.into}/${to.need} XP`;
    }
    await wait(500);
    if (info.levelUps && info.levelUps.length) {
      for (const lu of info.levelUps) await this.showLevelUp(lu.level, lu.rankChanged, lu.reward);
    }
  }

  // ---------- small overlays ----------
  toast(html, color = 'dark', dur = 3200) {
    const t = document.createElement('div');
    t.className = 'toast t-' + color;
    t.innerHTML = html;
    while(this.toastEl.children.length>=3)this.toastEl.firstElementChild.remove();
    this.toastEl.appendChild(t);
    requestAnimationFrame(() => t.classList.add('show'));
    setTimeout(() => {
      t.classList.remove('show');
      setTimeout(() => t.remove(), 400);
    }, dur);
  }

  badgeToast(b) {
    const rw = b.reward.gems ? `+${b.reward.gems} ${ICON.gem}` : `+${b.reward.coins} ${ICON.coin}`;
    this.toast(`<div class="bt-ico">${b.icon}</div><div><small>BADGE UNLOCKED!</small><b>${b.name}</b><span>${rw}</span></div>`, 'badge', 4200);
    audio.play('achievement');
  }

  flash(color = 'rgba(255,255,255,0.8)') {
    const f = this.flashEl;
    f.style.background = color;
    f.classList.remove('go');
    void f.offsetWidth;
    f.classList.add('go');
  }

  fade(on) {
    this.fadeEl.classList.toggle('on', on);
  }

  titleCard(level) {
    this.card.show(level);
  }
  hideTitleCard() {
    this.card.hide();
  }
  go() {
    this.card.splash('GO!');
  }

  // ---------- actions ----------
  async act(a, d, el) {
    const g = this.game;
    const s = this.save;
    if (!['item', 'toggle'].includes(a)) audio.play('click');
    switch (a) {
      case 'expedition': g.startLevel('1-'+d.id); break;
      case 'expeditionAnswer': g.level?.expedition?.answer(d.answer); break;
      case 'expeditionHint': if(g.level?.expedition?.question!==null){ const e=g.level.expedition; e.attempt++;e.renderQuestion(e.clues[e.question].hint);} break;
      case 'expeditionRead': if(g.level?.expedition?.question!==null){const e=g.level.expedition;audio.speak(e.clues[e.question].text);}break;
      case 'expeditionLater':g.level?.expedition?.close();break;
      case 'expansion': g.explore?.expansion.action(d.op,d); break;
      case 'modeHome': if (!g.quiz.open) { g.save.persist(true); g.showMenu(); g.input.reset(); } break;
      case 'inventory': g.explore?.openInventory(d.tab || 'bag'); break;
      case 'bagItem': g.explore?.chooseInventoryItem(d.id); break;
      case 'bagSlot': g.explore?.chooseInventorySlot(Number(d.i)); break;
      case 'hotbar': g.explore?.selectSlot(Number(d.i), true); break;
      case 'toggleHotbar': g.explore?.toggleHotbar(); break;
      case 'touchLayout': this.showTouchLayout(); break;
      case 'touchLayoutReset': g.input.layout.resetLayout(); break;
      case 'touchLayoutCancel': g.input.layout.finish(false); this.showPause(); break;
      case 'touchLayoutDone':
        if (g.input.layout.finish(true)) { g.resume(); this.toast('✓ Your controls are saved on this device', 'green'); }
        else this.modalEl.querySelector('.control-layout-status').textContent = 'Could not save here. Try Save again, or Cancel.';
        break;
      case 'craftItem': g.explore?.craftItem(d.id, d.shop==='1'); break;
      case 'recipeGroup': g.explore?.openInventory('craft', d.group); break;
      case 'claimProject': g.explore?.claimProject(d.id); break;
      case 'findTable': g.explore?.navigate('table'); break;
      case 'useItem': g.explore?.useSelected(); break;
      case 'stopGather': g.explore?.cancelGather(); break;
      case 'explore': g.startExplore(); break;
      case 'friends': g.accounts.showFriends(); break;
      case 'whoStart': g.accounts.startWho(); break;
      case 'whoLogin': g.accounts.submit(); break;
      case 'whoSkip': g.accounts.skip(); break;
      case 'whoAccept': g.accounts.submit({ accept: true }); break;
      case 'whoBack': g.accounts.back(); break;
      case 'whoFresh': g.accounts.submit({ fresh: true }); break;
      case 'switchPlayer': g.accounts.switchPlayer(); break;
      case 'copyRoomCode': g.accounts.copyRoomCode(g.mp?.room?.code); break;
      case 'joinWorld': g.accounts.joinWorld(d.code); break;
      case 'goMyWorld': g.startExplore(); break;
      case 'friendsPanel': g.explore?.friendsPanel(); break;
      // Phone HUD (1.6): ☰ More and the guide card's sheet. Town from the paused sheet resumes first.
      case 'exploreMore': g.explore?.moreSheet(); break;
      case 'moreTown': if (g.explore?.state === 'play') { g.resume(); g.explore.goHome(); } break;
      case 'exploreGuide': g.explore?.guideSheet(); break;
      case 'guideTrail': g.explore?.guideTrail(); break;
      case 'guideSkip': if (g.explore?.state === 'play') { g.explore.dismissGuide(); g.resume(); } break;
      case 'emoteRow': g.mp?.toggleEmotes(); break;
      case 'emote': g.mp?.emote(d.k); break;
      case 'joinLesson': g.mp?.joinLesson(d.id); break;
      case 'roomLock': g.mp?.lock(); break;
      case 'roomKick': g.mp?.kick(d.id); break;
      case 'roomUnkick': g.mp?.unkick(d.id); break;
      case 'roomReset': g.mp?.reset(); break;
      case 'roomRestore': g.mp?.restore(); break;
      case 'exploreHelp': g.explore?.help(); break;
      case 'dismissGuide': g.explore?.dismissGuide(); break;
      case 'navigate': g.explore?.navigate(d.id); break;
      case 'goToFriend': g.explore?.navigateToFriend(d.id); break;
      case 'journal': g.explore?.journal(); break;
      case 'exploreHome': if (!g.paused && g.explore?.state === 'play') g.explore.goHome(); break;
      case 'buildMode': g.explore?.toggleBuild(); break;
      case 'blockPick': if (!g.paused && g.explore?.state === 'play') g.explore.selectBlock(Number(d.i)); break;
      case 'placeBlock': g.explore?.placeBlock(); break;
      case 'removeBlock': g.explore?.removeBlock(); break;
      case 'learnStation': g.explore?.learn(d.id); break;
      case 'zoomIn': if (!g.paused && !g.quiz.open) g.camRig.zoom(-1); break;
      case 'zoomOut': if (!g.paused && !g.quiz.open) g.camRig.zoom(1); break;
      case 'zoomReset': if (!g.paused && !g.quiz.open) g.camRig.setDistance(7.5); break;
      case 'play':
        g.showWorlds(this.firstPlayableWorld());
        break;
      case 'home':
        g.showMenu();
        break;
      case 'map':
        g.showWorlds();
        break;
      case 'wprev':
        if (this.worldIdx > 0) this.showWorlds(this.worldIdx - 1);
        break;
      case 'wnext':
        if (this.worldIdx < WORLDS.length - 1) this.showWorlds(this.worldIdx + 1);
        break;
      case 'pick':
        if (!isLevelUnlocked(s, d.id)) {
          audio.play('wrong');
          el.classList.remove('shake');
          void el.offsetWidth;
          el.classList.add('shake');
          this.toast('🔒 Finish the level before this one to unlock it!', 'dark');
          return;
        }
        g.startLevel(d.id);
        break;
      case 'replay':
        g.startLevel(d.id);
        break;
      case 'pause':
        g.pause();
        break;
      case 'resume':
        g.resume();
        break;
      case 'restart':
        this.closeModal();
        g.restartLevel();
        break;
      case 'quit':
        this.closeModal();
        g.quitLevel();
        break;
      case 'pauseSettings':
        this.showSettings(true);
        break;
      case 'closeSettings':
        this.showPause();
        break;
      case 'locker':
        g.showLocker();
        break;
      case 'ltab':
        this.showLocker(d.tab);
        break;
      case 'item':
        g.lockerItem(d.cat, d.id, el);
        break;
      case 'tone':
        s.avatar.tone = parseInt(d.i, 10);
        g.save.persist();
        g.refreshCharacter();
        this.showLocker('look');
        break;
      case 'hair':
        s.avatar.hair = parseInt(d.i, 10);
        g.save.persist();
        g.refreshCharacter();
        this.showLocker('look');
        break;
      case 'hstyle':
        s.avatar.style = d.s;
        g.save.persist();
        g.refreshCharacter();
        this.showLocker('look');
        break;
      case 'badges':
        g.showBadges();
        break;
      case 'stats':
        g.showStats();
        break;
      case 'settings':
        g.showSettingsScreen();
        break;
      case 'toggle':
        s.settings[d.k] = !s.settings[d.k];
        el.classList.toggle('on', s.settings[d.k]);
        audio.play('click');
        if (d.k === 'haptics' && s.settings.haptics) haptics.play('correct');
        g.save.persist();
        if (d.k === 'worldListed') g.mp?.setListed(s.settings.worldListed);
        break;
      case 'quality':
        s.settings.quality = d.q;
        g.save.persist();
        g.applyQuality();
        this.refreshChips(el);
        break;
      case 'diff':
        s.settings.difficulty = d.d;
        g.save.persist();
        this.refreshChips(el);
        break;
      case 'topic': {
        const on = s.settings.topics[d.t] !== false;
        const enabledCount = Object.keys(TOPICS).filter((k) => s.settings.topics[k] !== false).length;
        if (on && enabledCount <= 1) {
          this.toast('Keep at least one topic switched on!', 'dark');
          return;
        }
        s.settings.topics[d.t] = !on;
        el.classList.toggle('on', !on);
        g.save.persist();
        break;
      }
      case 'ttable': {
        const t = parseInt(d.t, 10);
        const arr = s.settings.tables || (s.settings.tables = []);
        const i = arr.indexOf(t);
        if (i >= 0) arr.splice(i, 1);
        else arr.push(t);
        arr.sort((x, y) => x - y);
        el.classList.toggle('on', i < 0);
        g.save.persist();
        break;
      }
      case 'copyCode':
        try {
          await navigator.clipboard.writeText(s.cloudCode);
          this.toast('📋 Save code copied!', 'blue');
        } catch (e) {
          this.toast('Code: ' + s.cloudCode, 'blue', 6000);
        }
        break;
      case 'restore': {
        const inp = this.root.querySelector('.code-input') || this.modalEl.querySelector('.code-input');
        const msg = this.root.querySelector('.restore-msg');
        const ok = await this.confirm('Load a save?', 'This will replace the progress on this device with the saved game. Are you sure?', 'Load it', 'Cancel');
        if (!ok) return this.closeModal();
        this.closeModal();
        const r = await g.save.restoreFrom(inp.value);
        if (msg) msg.textContent = r.ok ? '✅ Save loaded!' : '❌ ' + r.msg;
        // A signed-in player keeps their card: the old game's progress moves onto it.
        if (r.ok && identity.loggedIn) {
          Object.assign(g.save.data, { cloudCode: identity.data.cloudCode, playerId: identity.data.playerId });
          g.save.persist(true);
        }
        if (r.ok) g.afterRestore();
        break;
      }
      case 'reset': {
        const ok = await this.confirm('Reset everything?', `This deletes ALL of ${escapeHtml(this.save.name)}’s progress, coins, skins and badges. This cannot be undone!`, 'Yes, reset', 'No way!');
        this.closeModal();
        if (!ok) return;
        const ok2 = await this.confirm('Are you REALLY sure?', 'Last chance! Everything will be gone.', 'Reset', 'Keep my stuff');
        this.closeModal();
        if (ok2) g.resetProgress();
        break;
      }
      case 'confirmYes':
        if (this.modalResolve) {
          const r = this.modalResolve;
          this.modalResolve = null;
          r(true);
        }
        break;
      case 'confirmNo':
        this.closeModal();
        break;
      case 'closeModal':
        this.closeModal();
        if (g.paused) g.resume();
        break;
      case 'daily':
        this.showDaily();
        break;
      case 'claimDaily':
        g.claimDaily(el);
        break;
      case 'introNext':
        if (this.introNext) this.introNext();
        break;
      case 'blitz':
        this.showBlitzSelect();
        break;
      case 'blitzGo':
        this.closeModal();
        g.startBlitz(d.mode);
        break;
      case 'practice': {
        s.settings.tables = [parseInt(d.t, 10)];
        g.save.persist();
        this.toast(`Practising the ${d.t} times table! (Change it in Settings any time.)`, 'purple');
        g.startBlitz('tables');
        break;
      }
    }
  }

  refreshChips(el) {
    const p = el.parentElement;
    p.querySelectorAll('.chip').forEach((c) => c.classList.toggle('on', c === el));
  }

  firstPlayableWorld() {
    const s = this.save;
    let w = 0;
    for (let i = 0; i < WORLDS.length; i++) if (isLevelUnlocked(s, `${WORLDS[i].id}-1`)) w = i;
    // prefer the world with the next unfinished level
    for (let i = 0; i < WORLDS.length; i++) {
      const W = WORLDS[i];
      for (const l of ['1', '2', '3', 'B']) {
        const id = `${W.id}-${l}`;
        if (isLevelUnlocked(s, id) && !s.levels[id]?.done) return i;
      }
    }
    return w;
  }
}

export const wait = (ms) => new Promise((r) => setTimeout(r, ms));
function countUp(el, to, ms) {
  const t0 = performance.now();
  const step = (t) => {
    const k = Math.min(1, (t - t0) / ms);
    el.textContent = fmtNum(Math.round(to * (1 - Math.pow(1 - k, 3))));
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
void THEMES;
void catalogItem;
